import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, useScroll, useMotionValueEvent, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";

const LINKS = [
  { to: "/", label: "INICIO", testid: "nav-link-inicio" },
  { to: "/como-funciona", label: "CÓMO FUNCIONA", testid: "nav-link-como-funciona" },
  { to: "/servicio", label: "SERVICIO", testid: "nav-link-servicio" },
  { to: "/para-comercios", label: "PARA COMERCIOS", testid: "nav-link-para-comercios" },
  { to: "/faq", label: "PREGUNTAS FRECUENTES", testid: "nav-link-faq" },
];

export default function Navbar() {
  const { scrollY } = useScroll();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  useMotionValueEvent(scrollY, "change", (v) => setScrolled(v > 40));

  return (
    <>
      <motion.header
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
        className={`fixed top-0 inset-x-0 z-[80] border-b transition-[padding,background-color,border-color] duration-500 backdrop-blur-xl ${
          scrolled ? "bg-night/85 border-silver/10 py-3" : "bg-night/40 border-transparent py-6"
        }`}
        data-testid="main-nav"
      >
        <div className="mx-auto max-w-[1500px] px-6 lg:px-12 flex items-center justify-between">
          <Link to="/" data-testid="nav-logo" className="flex items-baseline gap-2 group">
            <span className="font-oswald font-600 text-xl tracking-[0.18em] text-offwhite">APREA</span>
            <span className="font-mono text-[10px] tracking-[0.35em] text-steel group-hover:text-silver transition-colors duration-300">
              GESTORA
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-9">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={l.testid}
                className={({ isActive }) =>
                  `relative font-mono text-[11px] tracking-[0.22em] transition-colors duration-300 py-1 ${
                    isActive ? "text-offwhite" : "text-mist hover:text-silver"
                  } after:absolute after:left-0 after:bottom-0 after:h-px after:bg-silver after:transition-[width] after:duration-300 ${
                    isActive ? "after:w-full" : "after:w-0 hover:after:w-full"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <motion.span whileHover={{ y: -2 }} whileTap={{ scale: 0.97 }} className="inline-block">
              <Link
                to="/alta"
                data-testid="nav-cta-alta"
                className="group inline-flex items-center gap-2 bg-silver text-night font-mono text-[11px] tracking-[0.22em] px-6 py-3 hover:bg-offwhite transition-colors duration-300"
              >
                DARME DE ALTA
                <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </motion.span>
          </nav>

          <button
            data-testid="mobile-menu-button"
            onClick={() => setOpen(true)}
            className="lg:hidden text-silver p-2 -mr-2"
            aria-label="Abrir menú"
          >
            <Menu className="h-6 w-6" />
          </button>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-[100] bg-night/97 backdrop-blur-2xl flex flex-col"
            data-testid="mobile-menu"
          >
            <div className="flex items-center justify-between px-6 py-5 border-b border-silver/10">
              <span className="font-oswald text-xl tracking-[0.18em] text-offwhite">APREA</span>
              <button
                data-testid="mobile-menu-close"
                onClick={() => setOpen(false)}
                className="text-silver p-2"
                aria-label="Cerrar menú"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <nav className="flex-1 flex flex-col justify-center px-8 gap-2">
              {LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 + i * 0.07, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                >
                  <NavLink
                    to={l.to}
                    onClick={() => setOpen(false)}
                    data-testid={`mobile-${l.testid}`}
                    className={({ isActive }) =>
                      `block font-oswald text-4xl tracking-wide py-3 transition-colors duration-300 ${
                        isActive ? "text-offwhite" : "text-steel hover:text-silver"
                      }`
                    }
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.5 }}
                className="pt-8"
              >
                <Link
                  to="/alta"
                  onClick={() => setOpen(false)}
                  data-testid="mobile-nav-cta-alta"
                  className="inline-flex items-center gap-3 bg-silver text-night font-mono text-xs tracking-[0.25em] px-8 py-4"
                >
                  DARME DE ALTA <ArrowUpRight className="h-4 w-4" />
                </Link>
              </motion.div>
            </nav>
            <div className="px-8 pb-8 font-mono text-[10px] tracking-[0.3em] text-steel">
              GESTIÓN DE INCIDENCIAS — 24H HÁBILES
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
